import { useEffect, useMemo, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { inputClass, labelClass } from '../styles/class-utils'
import type { Product } from '../types'
import { searchProducts } from '../api/product-search'
import { resolveTenantId, subscribeTenantChanges } from '../lib/tenant-selection'

type ProductPickerMode = 'single' | 'multiple'

interface ProductPickerProps {
  value: string | string[]
  onChange: (value: string | string[]) => void
  mode?: ProductPickerMode
  tenantId?: string
  label?: string
  placeholder?: string
}

function normalizeValue(value: string | string[]): string[] {
  if (Array.isArray(value)) return value.filter(Boolean)
  return value ? [value] : []
}

export function ProductPicker({
  value,
  onChange,
  mode = 'single',
  tenantId,
  label = 'Product',
  placeholder = 'Search by name, SKU, or ID',
}: ProductPickerProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTenantId, setActiveTenantId] = useState(() => resolveTenantId(tenantId))
  const selectedIds = useMemo(() => normalizeValue(value), [value])

  useEffect(() => {
    setActiveTenantId(resolveTenantId(tenantId))
  }, [tenantId])

  useEffect(() => subscribeTenantChanges(() => {
    setActiveTenantId(resolveTenantId(tenantId))
  }), [tenantId])

  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed || !activeTenantId) {
      setResults([])
      return
    }

    const timeout = window.setTimeout(async () => {
      try {
        setLoading(true)
        const products = await searchProducts({ q: trimmed, tenantId: activeTenantId })
        setResults(products)
      } catch (error) {
        console.error('[ProductPicker] Failed to search products:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => window.clearTimeout(timeout)
  }, [query, activeTenantId])

  const selectedProducts = useMemo(
    () => results.filter((product) => selectedIds.includes(product.id)),
    [results, selectedIds],
  )

  const updateSelection = (nextIds: string[]) => {
    if (mode === 'single') {
      onChange(nextIds[0] ?? '')
      return
    }
    onChange(nextIds)
  }

  const selectProduct = (productId: string) => {
    if (mode === 'single') {
      updateSelection([productId])
      return
    }
    if (selectedIds.includes(productId)) return
    updateSelection([...selectedIds, productId])
  }

  const removeProduct = (productId: string) => {
    updateSelection(selectedIds.filter((id) => id !== productId))
  }

  return (
    <div className="space-y-2">
      <label className={labelClass}>{label}</label>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fn-text-muted" />
        <input
          type="text"
          className={`${inputClass} pl-9`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={activeTenantId ? placeholder : 'Select a store first'}
          disabled={!activeTenantId}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-fn-text-muted" />}
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((productId) => {
            const product = selectedProducts.find((item) => item.id === productId)
            return (
              <span
                key={productId}
                className="inline-flex items-center gap-1 rounded-full border border-fn-border bg-fn-muted px-2 py-1 text-xs text-fn-text"
              >
                {product?.name || productId}
                <button
                  type="button"
                  className="rounded p-0.5 text-fn-text-muted hover:bg-fn-hover"
                  onClick={() => removeProduct(productId)}
                  aria-label="Remove product"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )
          })}
        </div>
      )}

      {query.trim() && results.length > 0 && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-fn-border bg-fn-card">
          {results.map((product) => (
            <button
              key={product.id}
              type="button"
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-fn-hover ${selectedIds.includes(product.id) ? 'bg-fn-muted' : ''}`}
              onClick={() => selectProduct(product.id)}
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-fn-text">{product.name}</p>
                <p className="truncate text-xs text-fn-text-muted">{product.sku || product.id}</p>
              </div>
              {selectedIds.includes(product.id) && <span className="text-xs text-fn-primary">Selected</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
